import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  ReactElement,
} from "react";

import { useAppRouteId, useAppRouteParams } from "../../../../AppRoutes";
import { useContextMenuItemsViewsMenu } from "../../../../contextItems/hooks";
import {
  View,
  parseViewTitle,
  useUpdateView,
  useViews,
} from "../../../../domain/View";
import { ManagedTransaction } from "../../../../firebase/firestore";
import { useAppNotificationManager } from "../../../AppProvider/AppNotificationProvider";
import { useDialogContextMenuItemsView } from "../../../Dialog/DialogFirestore/DialogView/hooks";
import { SortableTmpMenuSet } from "../../helpers";

import styles from "./index.module.css";

import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { LoadingCoverWithoutText } from "@/components/LoadingCover";
import { translation } from "@/i18n";
import { nsSideNav } from "@/i18n/locales/sideNav";

const useTranslation = translation.use(nsSideNav);

/**
 * @description View一覧を表示し、編集や新規作成の操作機能を提供
 */
export const ViewsMenu: React.FC = () => {
  const { t } = useTranslation();
  const { versionId } = useAppRouteParams();
  const { hasPermission, isLoadedMe } = usePermissions();
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );
  const isSortDisabled = !hasVersionsMetaWritePermission;
  const [views, isLoadedViews] = useViews({});

  const [isCloseViews, setIsCloseViews] = useState(false);

  const sortedViews = useMemo(
    () =>
      [...views].sort((a, b) => {
        return Math.sign(a.sort - b.sort);
      }),
    [views]
  );

  const { renderDialogsView, getContextMenuItemsView } =
    useDialogContextMenuItemsView(sortedViews);

  const contextMenuItemsViews: ReactElement = useContextMenuItemsViewsMenu();
  const displayViewsMap = useMemo(() => {
    const result = new Map<string, View[]>();

    for (const view of sortedViews) {
      const { groupName } = parseViewTitle(view.title);
      const viewGroup = groupName || "__ungrouped__";
      const groupViews = result.get(viewGroup) || [];
      groupViews.push(view);
      result.set(viewGroup, groupViews);
    }

    // グループを辞書順にソート（グループなしを先頭に配置）
    const sortedResult = new Map<string, View[]>();
    const sortedKeys = Array.from(result.keys()).sort((a, b) => {
      // __ungrouped__を先頭に
      if (a === "__ungrouped__") return -1;
      if (b === "__ungrouped__") return 1;
      // それ以外は辞書順
      return a.localeCompare(b);
    });
    for (const key of sortedKeys) {
      sortedResult.set(key, result.get(key)!);
    }

    return sortedResult;
  }, [sortedViews]);

  if (!isLoadedViews || !isLoadedMe) {
    return <LoadingCoverWithoutText />;
  }

  return (
    <>
      <SortableTmpMenuSet
        id={"views"}
        text={t.sideNav.item.views()}
        iconName={isCloseViews ? "ChevronDown" : "ChevronUp"}
        onClick={() => {
          setIsCloseViews(!isCloseViews);
        }}
        contextMenuItems={contextMenuItemsViews}
      />
      {!isCloseViews &&
        Array.from(displayViewsMap).map(([viewGroup, views]) => {
          return (
            <ViewsSortableTmpMenuSet
              key={viewGroup}
              viewGroup={viewGroup}
              views={views}
              getContextMenuItemsView={getContextMenuItemsView}
              isSortDisabled={isSortDisabled}
            />
          );
        })}
      {renderDialogsView()}
    </>
  );
};

const ViewsSortableTmpMenuSet: React.FC<{
  viewGroup: string;
  views: View[];
  getContextMenuItemsView: ReturnType<
    typeof useDialogContextMenuItemsView
  >["getContextMenuItemsView"];
  isSortDisabled?: boolean;
}> = ({ viewGroup, views, getContextMenuItemsView, isSortDisabled }) => {
  const { t } = useTranslation();
  const { organizationId, versionId, viewId } = useAppRouteParams();
  const appRouteId = useAppRouteId();
  const updateView = useUpdateView();

  /**
   * dnd-kit
   */
  const [displayViews, setDisplayViews] = useState(views);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  const { showErrorNotification } = useAppNotificationManager();
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      if (active.id !== over.id) {
        const oldIndex = views.findIndex((item) => item.id === active.id);
        const newIndex = views.findIndex((item) => item.id === over.id);
        const newViews = arrayMove(views, oldIndex, newIndex);
        const newViewSorts = newViews
          .map(({ sort }) => sort)
          .sort((a, b) => Math.sign(a - b));
        const newViewsWithSort = newViews.map((view, index) => ({
          ...view,
          sort: newViewSorts[index],
        }));
        setDisplayViews(newViewsWithSort);
        try {
          await ManagedTransaction.runTransaction(async (transaction) => {
            // NOTE: 既存のドキュメント更新のため、updateDocでOK
            for (const view of newViewsWithSort) {
              await updateView(
                {
                  ...view,
                },
                { versionId, viewId: view.id },
                { transaction }
              );
            }
          });
        } catch (error) {
          setDisplayViews(views);
          showErrorNotification(t.sideNav.sortError.view(), error);
        }
      }
    },
    [t, views, updateView, versionId, showErrorNotification]
  );

  useEffect(() => {
    setDisplayViews(views);
  }, [views]);

  return (
    <div key={viewGroup}>
      {viewGroup !== "__ungrouped__" && (
        <div className={styles.viewGroup}>{viewGroup}</div>
      )}
      <div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={async (e) => {
            await handleDragEnd(e);
          }}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={displayViews.map(({ id }) => id)}
            disabled={isSortDisabled}
          >
            {displayViews.map((view) => {
              const { viewName, error } = parseViewTitle(view.title);
              // エラーがある場合はコンソールに警告を出力
              if (error) {
                console.warn(
                  `[ViewsMenu] Invalid view title format: "${view.title}" - ${error}`
                );
              }
              return (
                <SortableTmpMenuSet
                  key={view.id}
                  id={view.id}
                  text={viewName}
                  to={`/organizations/${organizationId}/versions/${versionId}/views/${view.id}`}
                  active={appRouteId === "view" && viewId === view.id}
                  contextMenuItems={
                    !isSortDisabled ? getContextMenuItemsView(view) : undefined
                  }
                  isSortable={!isSortDisabled}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
